import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOptimisticMutation } from '@noor/lib';
import {
  userApi,
  userKeys,
  type User,
  type Users,
  type UserInput,
  type UserRenameInput,
} from '@/entities/user';

/**
 * Attach a new staff member (create-by-phone).
 *
 * A plain `useMutation`, NOT optimistic — the rule in CLAUDE.md §3: the id is
 * server-minted, so a placeholder row would be unaddressable until the refetch
 * landed. The attach is also idempotent, so an existing member may come back
 * instead of a new one; an optimistic insert would then show a duplicate row
 * until the invalidation corrected it.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (input: UserInput) => userApi.create(input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

/** Variables for a rename: which member, and their new names. */
export type RenameUserVars = { id: User['id'] } & UserRenameInput;

/**
 * Rename a staff member.
 *
 * Optimistic — a field edit the server echoes back verbatim, so the patch
 * cannot disagree with the result. The per-member detail key is invalidated
 * alongside the list so a reopened edit form seeds from the fresh record
 * (CLAUDE.md §8, "Edit forms").
 */
export function useRenameUser() {
  const queryClient = useQueryClient();

  return useOptimisticMutation<User, RenameUserVars, Users>({
    meta: { suppressErrorToast: true },
    queryKey: userKeys.list(),
    mutationFn: ({ id, ...input }) => userApi.update(id, input),
    update: (data, { id, firstName, lastName }) => ({
      ...data,
      items: data.items.map((item) =>
        item.id === id
          ? {
              ...item,
              firstName,
              lastName,
              fullName: [lastName, firstName].filter(Boolean).join(' '),
            }
          : item,
      ),
    }),
    onSettled: (_data, _error, { id }) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

export function useUpdateUserLocations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, locationIds }: { id: string; locationIds: number[] }) =>
      userApi.updateEmployeeLocations(id, locationIds),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.employeeLocations(vars.id),
      });
    },
  });
}
